import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRow, MatTableDataSource } from '@angular/material/table';
import { SiteService } from '@app/app-state/site.service';
import { UserService } from '@app/app-state/user.service';
import * as backend from '@app/backend';
import { DataSourceSchema, DrillData, drillDataSourceCollectionSchema, blastDataSourceCollectionSchema, fielderDataSourceCollectionSchema,  mockDataCollection } from '@app/models/data-collection.model';
import { UnitConversionPipe, UnitDisplayPipe } from '@app/pipes';
import { combineLatest, debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import { DataCollectionStore } from '../data-collection.store';

type ExcelValue = string | number | boolean | Date | null | undefined | ExcelValue[] | { [key: string]: ExcelValue };
type DrillFieldValue = string | number | boolean | null;
type ExportColumn = { label: string; key: string; valueUnit?: string };

@Component({
    selector: 'app-drill-data-collection-view',
    templateUrl: './drill-data-collection-view.component.html',
    styleUrls: ['./drill-data-collection-view.component.scss'],
    standalone: false,
})
export class DrillDataCollectionViewComponent implements OnInit, OnDestroy {
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChildren(MatRow, { read: ElementRef }) rows!: QueryList<ElementRef<HTMLTableRowElement>>;
	@ViewChild('fileInput') fileInput: ElementRef;
	private selectedTimeline: string;
	private startDate: string;
	private endDate: string;
	private subDomainId: number;
	private siteId: number;
	private inputSubject: Subject<{apiV2DatasetId: number, key: string, value: string | number}> = new Subject();
	private searchSubject: Subject<string> = new Subject();
	private dataSourceCollectionResponse: DrillData[] = [];
	private subscriptions: Subscription = new Subscription();
	public search: string = '';
	private completionStatus: string = '';
	private loadRequestId: number = 0;
	private _active: boolean = false;

	
	public isFileUploaded:boolean = false;
	public newRowData: DrillData;
	public isNewRow: boolean = false;
	public domainId:number;
	public datasetNames: { blast: string, apiV2DatasetId: number }[] = [];
	public selectedDataset: { blast: string, apiV2DatasetId: number };
	public dataSourceSchema: DataSourceSchema[] = drillDataSourceCollectionSchema;
	public displayedColumns: string[] = this.dataSourceSchema.map(col => col.key);
	public dataSource: MatTableDataSource<DrillData>;
	public userUnitMeasurement: string;
	public loadingDrillData: boolean;
	public pageIndex: number = 0;
	public pageSize: number = 30;
	public totalCount: number = 0;

	public changeMeasurementArray = ['averageHoleDiameter','averageBenchHeight','averageHoleLength','totalDrillLength',
	'totalDrillDepth','averagePatternBurden','averageSpacing','averageSubdrill','shotVolume','powderFactorVolume','powderFactorWeight', 'averageFaceBurden', 'averageStemmingHeight', 'averageCharge'];

	public showModal: boolean = false;
	public filterValues = [{name: 'Marked as complete',value: false},{name: 'Not marked as complete',value: false}];
	@HostListener('document:click', ['$event'])
	onClick() {
		this.showModal = false;
	}
	private _type = 'drillData';
	public staticColumns = ['datasetName', 'blastReportUrl'];

	@Input()
	set type(value: string) {
		this._type = value;
		this.updateDataSourceSchema();
		this.displayedColumns = this.getDisplayedColumns();
	}

	get type(): string {
		return this._type;
	}

	@Input()
	set active(value: boolean) {
		if (this._active && !value) {
			this.loadRequestId++;
		}
		this._active = value;
	}
	
	constructor(
		private snackBar: MatSnackBar,
		private readonly userService: UserService,
		private readonly siteService: SiteService,
		private readonly dataCollectionStore: DataCollectionStore
	) {
		const combinedSub = combineLatest([
			siteService.domainId$,
			siteService.selectedTimeline$,
			siteService.startDate$,
			siteService.endDate$,
			siteService.subDomainId$,
			siteService.siteId$
		])
		.pipe(
			debounceTime(0),
			distinctUntilChanged((previous, current) => previous.every((value, index) => value === current[index]))
		)
		.subscribe(([domainId, selectedTimeline, startDate, endDate, subDomainId, siteId]) => {
			this.domainId = domainId;
			this.selectedTimeline = selectedTimeline;
			this.startDate = startDate;
			this.endDate = endDate;
			this.subDomainId = subDomainId;
			this.siteId = siteId;
			this.pageIndex = 0;
			if (this._active && this.domainId) {
				this.updateQueryState({ pageIndex: 0 });
				this.fetchDrillDataCollectionView();
			}
		});
		this.subscriptions.add(combinedSub);

		this.subscriptions.add(this.inputSubject.pipe(debounceTime(500)).subscribe(value => {
			this.updateInputField(value);
		}));

		this.subscriptions.add(this.searchSubject.pipe(debounceTime(300)).subscribe(value => {
			const search = value.trim();
			if (search === this.search) return;

			this.search = search;
			this.pageIndex = 0;
			if (this._active) {
				this.updateQueryState({ search, pageIndex: 0 });
				this.fetchDrillDataCollectionView();
			}
		}));

		this.subscriptions.add(this.dataCollectionStore.response$.subscribe(response => {
			if (response === null) return;

			this.newRowData = null;
			this.isNewRow = false;
			this.dataSourceCollectionResponse = (response?.data || []).map((item: DrillData) => ({ ...item, isRecordExist: false }));
			this.datasetNames = (response?.data || []).map((res: DrillData) => ({ apiV2DatasetId: res.apiV2DatasetId, blast: res.blast }));
			this.totalCount = response?.pagination?.totalCount || 0;
			this.dataSource = new MatTableDataSource(this.dataSourceCollectionResponse);
		}));

		this.subscriptions.add(this.dataCollectionStore.loading$.subscribe(loading => {
			this.loadingDrillData = loading;
		}));

		this.subscriptions.add(this.dataCollectionStore.query$.subscribe(query => {
			this.search = query.search;
			this.pageIndex = query.pageIndex;
			this.pageSize = query.pageSize;
			this.filterValues[0].value = query.completionStatus === 'completed';
			this.filterValues[1].value = query.completionStatus === 'incomplete';
		}));
	}

	ngOnInit(): void {
		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe((measurement: string) => {
			this.userUnitMeasurement = measurement;
		}));

	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	applyFilter(filterValue: string) {
		this.searchSubject.next(filterValue);
	}

	public updateDataSourceSchema() {
		switch (this.type) {
			case 'drillData':
				this.dataSourceSchema = drillDataSourceCollectionSchema;
				break;
			case 'blastData':
				this.dataSourceSchema = blastDataSourceCollectionSchema;
				break;
			case 'fielderData':
				this.dataSourceSchema = fielderDataSourceCollectionSchema;
				break;
		}
	}


	public getDisplayedColumns() {
		return [...this.staticColumns, ...this.dataSourceSchema.map(col => col.key)];
	}

	public getVideoUrls(element: DrillData): string[] {
		return Array.isArray(element?.videoUrls) ? element.videoUrls.filter(Boolean) : [];
	}

	public getVideoFileName(videoUrl: string): string {
		return decodeURIComponent(new URL(videoUrl).pathname.split('/').pop() ?? '');
	}

	public async downloadVideo(videoUrl: string): Promise<void> {
		try {
			const response = await fetch(videoUrl);
			if (!response.ok) throw new Error(`Video request failed with status ${response.status}`);

			const objectUrl = URL.createObjectURL(await response.blob());
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = this.getVideoFileName(videoUrl);
			link.click();
			URL.revokeObjectURL(objectUrl);
		} catch {
			this.snackBar.open('Unable to download video', ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 });
		}
	}


	convertValue(value: number, conversion: string): number {
		let convertedValue = value;
		if (conversion === 'm2ft') {
			convertedValue = value * 3.28084;
		} else if (conversion === 'ft2m') {
			convertedValue = value * 0.3048;
		}

		return parseFloat(convertedValue.toFixed(2));
	}

	private async fetchDrillDataCollectionView() {
		if (!this.domainId) return;

		const requestId = ++this.loadRequestId;
		this.dataCollectionStore.loading$.next(true);
		const query = this.dataCollectionStore.query$.value;
		try {
			const response = await backend.getDrillDataCollectionView(
				this.domainId,
				this.selectedTimeline,
				this.startDate,
				this.endDate,
				this.subDomainId,
				this.siteId,
				this.isFileUploaded,
				query.pageIndex + 1,
				query.pageSize,
				query.search,
				query.completionStatus
			);
			if (requestId !== this.loadRequestId || !this._active) return;

			backend.dataCollection$.next(response?.data || []);
			this.dataCollectionStore.response$.next(response);
		} catch (error) {
			if (requestId === this.loadRequestId) {
				console.error('Error fetching Drill Data Collection:', error);
			}
		} finally {
			if (requestId === this.loadRequestId) {
				this.dataCollectionStore.loading$.next(false);
			}
		}
	}

	public onPageChange(event: PageEvent) {
		if (!this._active) return;
		this.updateQueryState({ pageIndex: event.pageIndex, pageSize: event.pageSize });
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
		this.fetchDrillDataCollectionView();
	}

	onFieldInputClick(event: Event, col: DataSourceSchema, element: DrillData): void {
		const target = event.target as HTMLInputElement;
		if (!element.apiV2DatasetId) {
		  this.newRowData = element;
		}
		target.readOnly = col.readonly && !!element.apiV2DatasetId;
	}

	async onFieldInputChange(element: DrillData, key: string, value: DrillFieldValue, valueUnit: string, type: string)
	{
		if (!element.apiV2DatasetId) return;
		if (key && value) {
			let tranformedValue: string | number = value as string | number;
			if (type === 'number') {
				tranformedValue = (new UnitConversionPipe()).transform(Number(tranformedValue), this.userUnitMeasurement, valueUnit, true);
			}
			if (!this.isNewRow) {
				const updatedData = {
					apiV2DatasetId: element.apiV2DatasetId,
					key:key,
					value: tranformedValue
				}
				this.inputSubject.next(updatedData);
			}
			element[key] = type === 'number' ? Number(tranformedValue) : tranformedValue;
		}
	}

	private async updateInputField(updatedData) {
		const response = await backend.updateDrillDataCollectionField(updatedData.apiV2DatasetId, updatedData.key, updatedData.value);
		if (response?.message) {
			this.snackBar.open(response.message, ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000});
		}
	}

	public onDatasetSelect(event){
		const value = event.value;
		this.newRowData.blast = value.blast;
		this.newRowData.apiV2DatasetId =  value.apiV2DatasetId;
	}

	async downloadToExcel(data: string) {
		try {
			const columnOrder: ExportColumn[] = [{label: 'Project', key: 'datasetName'},{label: 'Blast Report URL', key: 'blastReportUrl'}, ...this.dataSourceSchema];
			const dataSource = data === "exportExcel" ? await this.getAllDataForExport() : mockDataCollection;
			if(!dataSource) return;
			const modifiedDataSource = dataSource.map(item => {
				const result = {};
				columnOrder.forEach(schema => {
				const key = schema.key;
				const value = item[key];
				let newKey = schema.label;

				if (schema.valueUnit) {
					newKey = `${(new UnitDisplayPipe()).transform(schema.label, this.userUnitMeasurement, schema.valueUnit)}`;
				}

				if (value !== null && value !== undefined && schema.valueUnit) {
					result[newKey] = (new UnitConversionPipe()).transform(Number(value), this.userUnitMeasurement, schema.valueUnit);
				} else {
					result[newKey] = this.formatExcelValue(value);
				}
				});

				return result;
			});
			const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(modifiedDataSource);
			const wb: XLSX.WorkBook = XLSX.utils.book_new();

			XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

			const fileName = data === "exportData" ?  'SheetJS.xlsx' : 'Sample.xlsx';
			XLSX.writeFile(wb, fileName);
		} catch {
			this.snackBar.open('Unable to export data. Please try again.', ' ', {
				horizontalPosition: 'right',
				verticalPosition: 'top',
				duration: 3 * 1000
			});
		}
	}

	private async getAllDataForExport(): Promise<DrillData[]> {
		const exportDomainId = this.domainId;
		if (!exportDomainId) return [];

		const exportTimeline = this.selectedTimeline;
		const exportStartDate = this.startDate;
		const exportEndDate = this.endDate;
		const exportSubDomainId = this.subDomainId;
		const exportSiteId = this.siteId;
		const exportFileUploaded = this.isFileUploaded;
		const query = { ...this.dataCollectionStore.query$.value };
		const rows: DrillData[] = [];
		const perPage = Math.max(query.pageSize, 100);
		let page = 1;
		let totalCount = 0;

		do {
			const response = await backend.getDrillDataCollectionView(
				exportDomainId,
				exportTimeline,
				exportStartDate,
				exportEndDate,
				exportSubDomainId,
				exportSiteId,
				exportFileUploaded,
				page,
				perPage,
				query.search,
				query.completionStatus
			);
			const pageRows = response?.data || [];
			rows.push(...pageRows);
			totalCount = response?.pagination?.totalCount || rows.length;
			page += 1;
			if (pageRows.length === 0) break;
		} while (rows.length < totalCount);

		return rows;
	}

	private formatExcelValue(value: ExcelValue): ExcelValue {
		if (!Array.isArray(value)) return value;

		return value.join(', ');
	}

	importAsExcel() {
		this.fileInput.nativeElement.click();
	}

	onFileChange(event: Event): void {
		const file = (event.target as HTMLInputElement).files[0];
		this.uploadFile(file);
	}

	async uploadFile(file: File) {
		const response = await backend.uploadFile(file)
		if(response) {
			this.fileInput.nativeElement.value = null;

			if (response?.message) {
				this.snackBar.open(response.message, ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000});
			}

			this.isFileUploaded = true;
			if (!this._active) return;
			this.fetchDrillDataCollectionView();
		}
		else {
			this.snackBar.open('Error importing data: Unsupported file format', ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000});
		}
	}

	onSaveOrAddRow(): void {
		this.isNewRow ? this.saveRow() : this.addRow() ;
	}

	async addRow(): Promise<void> {
		if (this.siteId) {
			try {
				const datasets = await backend.fetchSiteDatasetsList(this.siteId);
				this.datasetNames = (datasets || []).map((dataset: { id: number; name: string }) => ({
					apiV2DatasetId: dataset.id,
					blast: dataset.name
				}));
			} catch {
				this.snackBar.open('Unable to load datasets', ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 });
				return;
			}
		}

		this.isNewRow = true;
		const newRowData = {} as DrillData;
		this.dataSourceSchema.forEach((col) => {
			newRowData[col.key] = '';
		});

		const currentData = this.dataSource.data.slice();
		currentData.splice(0, 0, newRowData);

		this.newRowData = newRowData;
		this.dataSource.data = currentData;
		this.scrollToFirstIndex();
	}

	scrollToFirstIndex(): void {
		let elem = this.rows.first;
		elem?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
	}

	removeRow(): void {
		this.isNewRow = false;
		const index = this.dataSource.data.findIndex(
			(row) => row === this.newRowData
		);
		if (index !== -1) {
			this.dataSource.data.splice(index, 1);
			this.dataSource._updateChangeSubscription();
		}
		this.newRowData = null;
	}

	async saveRow() {
		if (this.newRowData?.blast) {
			this.isNewRow = false;
			this.newRowData.blast = this.selectedDataset.blast;
			this.newRowData.apiV2DatasetId = this.selectedDataset.apiV2DatasetId;

			const response = await backend.addDataCollectionField(this.newRowData, this.domainId);
			if (response?.message) {
				this.snackBar.open(response.message, '', {
					horizontalPosition: 'right',
					verticalPosition: 'top',
					duration: 3 * 1000,
				});
				this.newRowData.isRecordExist = true;
				this.selectedDataset = null;
			}
		}
		else {
			if (this.newRowData) {
				this.snackBar.open('Please fill the current Drill & Blast row', ' ', { horizontalPosition: 'right', verticalPosition: 'top', duration: 3 * 1000});
				return;
			}
		}
	}

	public onApplyFilter() {
		const allTrue = this.filterValues.every(item => item.value === true);
		const allFalse = this.filterValues.every(item => item.value === false);

		if (allTrue || allFalse) {
			this.completionStatus = '';
		} else {
			const selected = this.filterValues.find(item => item.value === true);
			this.completionStatus = selected?.name === 'Marked as complete' ? 'completed' : 'incomplete';
		}

		if (!this._active) return;
		this.updateQueryState({ completionStatus: this.completionStatus, pageIndex: 0 });
		this.pageIndex = 0;
		this.fetchDrillDataCollectionView();
	}

	private updateQueryState(update: Partial<backend.DataCollectionQueryState>): void {
		this.dataCollectionStore.query$.next({ ...this.dataCollectionStore.query$.value, ...update });
	}
}
